import { StyleSheet, View } from "react-native";
import { File, FileStatus, StoragePathType } from "../../../graphql/generated/graphql";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ResourceType } from "../../../contexts/ActiveCourseContext";
import ResourceCard from "./ResourceCard";
import FileUploadComponent from "../../file/FileUploadComponent/FileUploadComponent";
import { ProcessedFile } from "../../../hooks/useFileUpload";

interface StudentFileUploadProps {
  resource: { resourceType: ResourceType; resourceId: string };
  filesState: [Partial<File>[], Dispatch<SetStateAction<Partial<File>[]>>];
  storagePath: StoragePathType;
  onSubmitFiles: () => Promise<void>;
}

const StudentFileUpload = ({ resource, filesState, storagePath, onSubmitFiles }: StudentFileUploadProps) => {
  const [pendingSubmission, setPendingSubmission] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [files, setFiles] = filesState;

  useEffect(() => {
    setFileUploadError(null);
  }, [resource?.resourceId]);

  const handleDeleteFile = (fileId) => {
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handleBeforeUpload = () => {
    setPendingSubmission(true);
    setFileUploadError(null);
  };

  const handleAfterUpload = (processedFilesArray: ProcessedFile[]) => {
    setFiles((prev) => [
      ...prev,
      ...processedFilesArray.map((processedFile) => ({
        id: processedFile.file.id,
        fileName: processedFile.file.fileName,
        fileURL: processedFile.file.fileURL,
        created: new Date(),
        status: FileStatus.Pending,
      })),
    ]);
    setPendingSubmission(false);
  };

  const handleFileUploadError = () => {
    setPendingSubmission(false);
    setFileUploadError(
      "Failed to upload file. Please ensure the file is less than 2MB and is a supported format (PDF, DOCX, XLSX, PPTX, XLS, DOC, CSV, TXT, MP4)."
    );
  };

  const handleSubmitFiles = async () => {
    try {
      await onSubmitFiles();
      setPendingSubmission(false);
    } catch (error) {
      console.error("Error submitting files:", error);
    }
  };

  return (
    <>
      <FileUploadComponent
        storagePath={storagePath}
        beforeUpload={handleBeforeUpload}
        afterUpload={handleAfterUpload}
        onError={handleFileUploadError}
        multiple={true}
      />
      {fileUploadError && (
        <View role="alert" accessibilityLiveRegion="assertive" style={{ marginTop: 8 }}>
          <CustomText text={fileUploadError} size="xs" style={{ color: "#E53E3E" }} useTranslationText={false} />
        </View>
      )}

      <CustomSpacing type="vertical" size="m" />

      {files.length > 0 && files.some((file) => file.status === FileStatus.Pending) && (
        <>
          <View style={styles.filesContainer}>
            {files
              .filter((file) => file.status === FileStatus.Pending)
              .map((file) => (
                <ResourceCard
                  key={file.id}
                  file={file}
                  showDate={false}
                  showDelete={true}
                  onDelete={() => handleDeleteFile(file.id)}
                />
              ))}
          </View>
          <CustomSpacing type="vertical" size="m" />
        </>
      )}

      {/* Only show submit button if there are files */}
      {files.length > 0 && files.some((file) => file.status === FileStatus.Pending) && (
        <>
          {resource.resourceType === ResourceType.ACTIVITY_ASSIGNMENT && (
            <>
              <CustomText
                text="You can submit your assignment once. You cannot change your submission once submitted."
                size="s"
                style={styles.submissionInfoText}
                useTranslationText={false}
              />

              <CustomSpacing type="vertical" size="m" />
            </>
          )}

          <CustomButton
            disabled={pendingSubmission}
            text={resource.resourceType === ResourceType.ACTIVITY_PROJECT ? "Submit Project" : "Submit Assignment"}
            size="m"
            onPress={handleSubmitFiles}
            style={pendingSubmission ? styles.disabledButton : styles.submitButton}
            textStyle={pendingSubmission ? { color: "#475467" } : { color: "#FFFFFF" }}
          />
        </>
      )}
    </>
  );
};

export default StudentFileUpload;

const styles = StyleSheet.create({
  submissionInfoText: {
    color: "#6B7280",
  },
  submitButton: {
    backgroundColor: "#3E68FE",
    width: "100%",
  },
  disabledButton: {
    backgroundColor: "#EAECF0",
    width: "100%",
  },
  filesContainer: {
    marginBottom: 10,
    flexDirection: "column",
    gap: 10,
    width: "100%",
  },
});
